import type { DateRange } from "@repo/app-types/entities/dateRange";
import { InterviewDecision } from "@repo/app-types/entities/interview";
import {
  RecruitingInsightsPendingDecision,
  type RecruitingInsightsDecision,
  type RecruitingInsightsDecisionBucket,
  type RecruitingInsightsDecisionByScoreBucket,
  type RecruitingInsightsResponse,
  type RecruitingInsightsScoreBucket,
  type RecruitingInsightsTimeSeriesPoint,
} from "@repo/app-types/entities/recruitingInsights";
import { ChartCard } from "@repo/ui/Cards/chartCard";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { DateTime, Duration } from "luxon";
import { useMemo, type FC } from "react";
import { useSearchParams } from "react-router";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useRecruitingInsightsQuery } from "../../api/entities/recruitingInsights.js";
import { DateRangeFilterItem } from "../filters/dateRangeFilterItem.js";

// Range is encoded across two search params so URLs stay legible — e.g.
// `?length=P30D` for "last 30 days" or `?length=P30D&end=2026-04-17T00:00:00.000Z`
// for an anchored range.
const LENGTH_SEARCH_PARAM = "length";
const END_SEARCH_PARAM = "end";

// Default to the last 30 days when the URL has no range params.
const DEFAULT_RANGE: DateRange = {
  length: Duration.fromObject({ days: 30 }),
  end: undefined,
};

const TICK_STYLE = { fontSize: 12, fontWeight: 500, fill: "#475569" };
const ADVANCED_COLOR = "var(--color-green-600)";
const REJECTED_COLOR = "var(--color-red-600)";
const PENDING_COLOR = "var(--color-gray-400)";
const PRIMARY_COLOR = "var(--color-blue-600)";

interface RecruitingInsightsTabProps {
  projectId: string;
}

export const RecruitingInsightsTab: FC<RecruitingInsightsTabProps> = ({
  projectId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const now = useNow();

  // URL-driven range state. Absent / invalid params fall back to the
  // last-30-days default.
  const range = useMemo(
    () => parseRangeFromSearchParams(searchParams) ?? DEFAULT_RANGE,
    [searchParams],
  );

  const { from, to } = useMemo(() => resolveRangeBounds(range), [range]);

  const { data, isLoading, isFetching, isError, refetch } =
    useRecruitingInsightsQuery({
      projectId,
      from,
      to,
      timeZone: now.zoneName,
    });

  const handleRangeChange = (next: DateRange) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(LENGTH_SEARCH_PARAM, next.length.toISO());
    if (next.end) {
      nextParams.set(END_SEARCH_PARAM, next.end.toUTC().toISO());
    } else {
      nextParams.delete(END_SEARCH_PARAM);
    }
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div className="mx-auto w-full max-w-4xl space-y-4 py-4">
      <div className="flex items-center gap-2">
        <DateRangeFilterItem
          hasBasisPicker={false}
          value={range}
          onChange={handleRangeChange}
        />
        {isFetching && !isLoading ? (
          <Loader style={LoaderStyle.SPINNER} />
        ) : null}
      </div>

      {isError ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Couldn&apos;t load insights.{" "}
          <button
            type="button"
            className="font-medium underline"
            onClick={() => refetch()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {isLoading || !data ? (
        <DashboardSkeleton />
      ) : (
        <DashboardContent data={data} />
      )}
    </div>
  );
};

const DashboardContent: FC<{ data: RecruitingInsightsResponse }> = ({
  data,
}) => {
  return (
    <>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiTile
          label="Completion rate"
          value={formatPercent(data.kpis.completionRate)}
        />
        <KpiTile
          label="Average interview duration"
          value={formatDurationMinutes(data.kpis.averageDurationSeconds)}
        />
        <KpiTile
          label="Total interviews conducted"
          value={formatDurationHours(data.kpis.totalDurationSeconds)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Interviews over time">
          <InterviewsOverTimeChart points={data.timeSeries} />
        </ChartCard>

        <ChartCard title="Interview scores">
          <ScoreHistogramChart points={data.scoreHistogram} />
        </ChartCard>

        <ChartCard title="Interview decisions">
          <DecisionDonutChart points={data.decisionBreakdown} />
        </ChartCard>

        <ChartCard title="Interview decisions by score">
          <DecisionsByScoreChart points={data.decisionsByScore} />
        </ChartCard>
      </div>
    </>
  );
};

const DashboardSkeleton: FC = () => (
  <>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-24 animate-pulse rounded-md bg-gray-100 shadow-sm ring-1 ring-black/5"
        />
      ))}
    </div>
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="h-72 animate-pulse rounded-md bg-gray-100 shadow-sm ring-1 ring-black/5"
        />
      ))}
    </div>
  </>
);

const KpiTile: FC<{
  label: string;
  value: string;
}> = ({ label, value }) => (
  <div className="rounded-md bg-white px-4 py-4 shadow-sm ring-1 ring-black/5">
    <div className="text-xs font-medium text-muted-foreground">{label}</div>
    <div className="mt-1 text-2xl font-semibold text-foreground">{value}</div>
  </div>
);

const EmptyChart: FC<{ message?: string }> = ({
  message = "No data in range",
}) => (
  <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
    {message}
  </div>
);

const InterviewsOverTimeChart: FC<{
  points: readonly RecruitingInsightsTimeSeriesPoint[];
}> = ({ points }) => {
  const data = useMemo(
    () =>
      points.map((p) => ({
        // Parse the calendar-day string in the viewer's local zone so
        // tick / tooltip formatters render the same calendar day the API
        // bucketed by. Parsing as UTC would render as the previous day
        // for any viewer west of UTC.
        dayMs: DateTime.fromISO(p.day).toMillis(),
        count: p.count,
      })),
    [points],
  );

  if (data.length === 0) {
    return <EmptyChart />;
  }

  return (
    <ResponsiveContainer width="100%" height={250}>
      <LineChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="dayMs"
          type="number"
          scale="time"
          domain={["dataMin", "dataMax"]}
          tick={TICK_STYLE}
          tickMargin={8}
          tickFormatter={formatDayTick}
        />
        <YAxis
          allowDecimals={false}
          tick={TICK_STYLE}
          tickMargin={8}
          width={40}
          tickFormatter={formatCountTick}
        />
        <Tooltip
          contentStyle={tooltipContentStyle}
          labelFormatter={(v) =>
            typeof v === "number" ? formatDayTooltipLabel(v) : String(v)
          }
          formatter={formatCountTooltip}
        />
        <Line
          type="monotone"
          dataKey="count"
          stroke={PRIMARY_COLOR}
          strokeWidth={2}
          dot={{ r: 3 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

const ScoreHistogramChart: FC<{
  points: readonly RecruitingInsightsScoreBucket[];
}> = ({ points }) => {
  const data = useMemo(() => fillScoreBuckets(points), [points]);
  const total = data.reduce((sum, p) => sum + p.count, 0);

  if (total === 0) {
    return <EmptyChart />;
  }

  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="score" tick={TICK_STYLE} tickMargin={8} />
        <YAxis
          allowDecimals={false}
          tick={TICK_STYLE}
          tickMargin={8}
          width={40}
          tickFormatter={formatCountTick}
        />
        <Tooltip
          contentStyle={tooltipContentStyle}
          formatter={formatCountTooltip}
        />
        <Bar dataKey="count" fill={PRIMARY_COLOR} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const DECISION_LABELS: Record<RecruitingInsightsDecision, string> = {
  [InterviewDecision.Advanced]: "Advanced",
  [InterviewDecision.Rejected]: "Rejected",
  [RecruitingInsightsPendingDecision]: "Pending",
};

const DECISION_COLORS: Record<RecruitingInsightsDecision, string> = {
  [InterviewDecision.Advanced]: ADVANCED_COLOR,
  [InterviewDecision.Rejected]: REJECTED_COLOR,
  [RecruitingInsightsPendingDecision]: PENDING_COLOR,
};

// Display order — top of legend / left of stack.
const DECISION_ORDER: readonly RecruitingInsightsDecision[] = [
  InterviewDecision.Advanced,
  InterviewDecision.Rejected,
  RecruitingInsightsPendingDecision,
];

const DecisionDonutChart: FC<{
  points: readonly RecruitingInsightsDecisionBucket[];
}> = ({ points }) => {
  const total = points.reduce((sum, p) => sum + p.count, 0);

  if (total === 0) {
    return <EmptyChart message="No completed interviews in range" />;
  }

  const data = DECISION_ORDER.flatMap((decision) => {
    const found = points.find((p) => p.decision === decision);
    return found
      ? [
          {
            decision,
            count: found.count,
            label: DECISION_LABELS[decision],
          },
        ]
      : [];
  });

  return (
    <ResponsiveContainer width="100%" height={250}>
      <PieChart>
        <Pie
          data={data}
          dataKey="count"
          nameKey="label"
          innerRadius={60}
          outerRadius={90}
          paddingAngle={1}
          isAnimationActive={false}
          label={({ percent }: { percent?: number }) =>
            percent !== undefined ? `${(percent * 100).toFixed(1)}%` : ""
          }
        >
          {data.map((entry, i) => (
            <Cell key={i} fill={DECISION_COLORS[entry.decision]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={tooltipContentStyle}
          formatter={formatCountTooltip}
        />
        <Legend
          formatter={(value: string) => (
            <span className="text-sm text-foreground">{value}</span>
          )}
        />
      </PieChart>
    </ResponsiveContainer>
  );
};

const DecisionsByScoreChart: FC<{
  points: readonly RecruitingInsightsDecisionByScoreBucket[];
}> = ({ points }) => {
  const data = useMemo(() => fillDecisionByScoreBuckets(points), [points]);
  const total = data.reduce(
    (sum, p) => sum + p.advanced + p.rejected + p.pending,
    0,
  );

  if (total === 0) {
    return <EmptyChart message="No scored completed interviews in range" />;
  }

  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={data} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="score" tick={TICK_STYLE} tickMargin={8} />
        <YAxis
          allowDecimals={false}
          tick={TICK_STYLE}
          tickMargin={8}
          width={40}
          tickFormatter={formatCountTick}
        />
        <Tooltip
          contentStyle={tooltipContentStyle}
          formatter={formatCountTooltip}
        />
        <Legend
          formatter={(value: string) => (
            <span className="text-sm text-foreground">{value}</span>
          )}
        />
        <Bar
          dataKey="advanced"
          name={DECISION_LABELS[InterviewDecision.Advanced]}
          stackId="decision"
          fill={ADVANCED_COLOR}
          radius={[4, 4, 0, 0]}
        />
        <Bar
          dataKey="rejected"
          name={DECISION_LABELS[InterviewDecision.Rejected]}
          stackId="decision"
          fill={REJECTED_COLOR}
          radius={[4, 4, 0, 0]}
        />
        <Bar
          dataKey="pending"
          name={DECISION_LABELS[RecruitingInsightsPendingDecision]}
          stackId="decision"
          fill={PENDING_COLOR}
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

const tooltipContentStyle = {
  padding: "6px 10px",
  borderRadius: "6px",
  borderColor: "#d1d5db",
  fontSize: 12,
};

function fillScoreBuckets(
  points: readonly RecruitingInsightsScoreBucket[],
): { score: number; count: number }[] {
  const byScore = new Map(points.map((p) => [p.score, p.count]));
  return [1, 2, 3, 4, 5].map((score) => ({
    score,
    count: byScore.get(score) ?? 0,
  }));
}

function fillDecisionByScoreBuckets(
  points: readonly RecruitingInsightsDecisionByScoreBucket[],
): {
  score: number;
  advanced: number;
  rejected: number;
  pending: number;
}[] {
  const counts = new Map<
    number,
    { advanced: number; rejected: number; pending: number }
  >();
  for (const p of points) {
    const bucket = counts.get(p.score) ?? {
      advanced: 0,
      rejected: 0,
      pending: 0,
    };
    if (p.decision === InterviewDecision.Advanced) {
      bucket.advanced = p.count;
    } else if (p.decision === InterviewDecision.Rejected) {
      bucket.rejected = p.count;
    } else if (p.decision === RecruitingInsightsPendingDecision) {
      bucket.pending = p.count;
    }
    counts.set(p.score, bucket);
  }
  return [1, 2, 3, 4, 5].map((score) => ({
    score,
    ...(counts.get(score) ?? { advanced: 0, rejected: 0, pending: 0 }),
  }));
}

// Y-axis tick formatter — keeps tick labels short by rendering counts in
// compact notation (e.g. `1.2K`, `34K`, `1.2M`) with at most 2 significant
// digits. Small counts (< 1000) stay as plain integers.
const COMPACT_COUNT_FORMATTER = new Intl.NumberFormat(undefined, {
  notation: "compact",
  maximumSignificantDigits: 2,
});

function formatCountTick(value: unknown): string {
  return typeof value === "number"
    ? COMPACT_COUNT_FORMATTER.format(value)
    : String(value);
}

// Tooltip value formatter — recharts hands the value plus the series name;
// returning a [value, name] tuple keeps the label intact while reformatting
// just the number.
function formatCountTooltip(value: unknown, name: unknown): [string, string] {
  const formatted =
    typeof value === "number" ? value.toLocaleString() : String(value);
  return [formatted, String(name)];
}

function formatDayTick(ms: number): string {
  return DateTime.fromMillis(ms).toFormat("MMM d");
}

function formatDayTooltipLabel(ms: number): string {
  return DateTime.fromMillis(ms).toFormat("MMM d, yyyy");
}

function formatPercent(value: number | null): string {
  if (value === null) return "—";
  return `${(value * 100).toFixed(1)}%`;
}

function formatDurationMinutes(seconds: number | null): string {
  if (seconds === null || seconds <= 0) return "—";

  const duration = Duration.fromObject({ seconds }).shiftTo(
    "minutes",
    "seconds",
  );

  return duration.toHuman({ unitDisplay: "short", maximumFractionDigits: 0 });
}

function formatDurationHours(seconds: number): string {
  if (seconds <= 0) return "0";

  const duration = Duration.fromObject({ seconds });

  if (duration.as("hours") < 1) {
    return duration
      .shiftTo("minutes")
      .toHuman({ unitDisplay: "long", maximumFractionDigits: 0 });
  }

  return duration
    .shiftTo("hours")
    .toHuman({ unitDisplay: "long", maximumFractionDigits: 1 });
}

// Reads `length` (ISO-8601 duration, required) and `end` (ISO-8601
// instant, optional) from the URL. Returns undefined if `length` is
// missing or unparseable so the caller can fall back to a default range.
// A bad `end` is ignored — the duration alone is still meaningful.
function parseRangeFromSearchParams(
  searchParams: URLSearchParams,
): DateRange | undefined {
  const rawLength = searchParams.get(LENGTH_SEARCH_PARAM);
  if (rawLength === null) {
    return undefined;
  }
  const length = Duration.fromISO(rawLength);
  if (!length.isValid) {
    return undefined;
  }

  let end: DateTime<true> | undefined;
  const rawEnd = searchParams.get(END_SEARCH_PARAM);
  if (rawEnd !== null) {
    const parsedEnd = DateTime.fromISO(rawEnd, { zone: "utc" });
    if (parsedEnd.isValid) {
      end = parsedEnd;
    }
  }

  return { length, end };
}

// Resolve the `DateRange` into the inclusive-from / exclusive-to ISO pair
// expected by the API.
function resolveRangeBounds(range: DateRange): {
  from: string | undefined;
  to: string | undefined;
} {
  const end = range.end ?? DateTime.utc();
  const start = end.minus(range.length);
  // Guard: `DateRange.length` may be a negative-or-zero duration if
  // mis-stored; in that case skip filtering.
  if (!start.isValid || start >= end) {
    return { from: undefined, to: undefined };
  }
  return { from: start.toUTC().toISO(), to: end.toUTC().toISO() };
}
